import React from 'react';
import { useSpeechSynthesis, type SpeechStatus } from '../../hooks/useSpeechSynthesis';
import { Volume2, Pause, Square, Play } from 'lucide-react';

interface TextToSpeechPlayerProps {
  /** Markdown or plain text to read aloud */
  text: string;
}

const statusLabel: Record<SpeechStatus, string> = {
  idle: 'Escuchar artículo',
  speaking: 'Reproduciendo…',
  paused: 'En pausa',
};

const TextToSpeechPlayer: React.FC<TextToSpeechPlayerProps> = ({ text }) => {
  const { status, progress, supported, speak, pause, resume, stop } =
    useSpeechSynthesis(text);

  if (!supported) return null;

  const progressPct = Math.round(progress * 100);

  return (
    <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-contessa-50/70 border border-contessa-200/60 select-none">
      {/* Play / Pause toggle */}
      {status === 'idle' && (
        <button
          onClick={speak}
          className="flex items-center gap-1.5 text-contessa-600 hover:text-contessa-700 transition text-xs font-medium"
          aria-label="Escuchar artículo"
        >
          <Volume2 className="h-3.5 w-3.5" />
          <span>{statusLabel.idle}</span>
        </button>
      )}

      {status === 'speaking' && (
        <button
          onClick={pause}
          className="flex items-center gap-1.5 text-contessa-600 hover:text-contessa-700 transition text-xs font-medium"
          aria-label="Pausar"
        >
          <Pause className="h-3.5 w-3.5" />
          <span>{statusLabel.speaking}</span>
        </button>
      )}

      {status === 'paused' && (
        <button
          onClick={resume}
          className="flex items-center gap-1.5 text-contessa-600 hover:text-contessa-700 transition text-xs font-medium"
          aria-label="Continuar"
        >
          <Play className="h-3.5 w-3.5" />
          <span>{statusLabel.paused}</span>
        </button>
      )}

      {/* Progress bar — inline, only when active */}
      {status !== 'idle' && (
        <>
          <div className="w-16 h-1 bg-contessa-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-contessa-400 rounded-full transition-[width] duration-300 ease-linear"
              style={{ width: `${progressPct}%` }}
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <button
            onClick={stop}
            className="text-contessa-400 hover:text-contessa-600 transition"
            aria-label="Detener"
          >
            <Square className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
};

export default TextToSpeechPlayer;
